import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Search, GraduationCap, Briefcase, DollarSign, Home, MapPin, School,
  Rocket, Shield, Compass, ArrowRight, Layers, Database, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });

const ICONS = {
  major: GraduationCap, career: Briefcase, income: DollarSign, lifestyle: Home,
  geography: MapPin, school: School, entrepreneur: Rocket, military: Shield, unsure: Compass,
};
const HUES = {
  major: '#22d3ee', career: '#38bdf8', income: '#fbbf24', lifestyle: '#c084fc',
  geography: '#2dd4bf', school: '#a78bfa', entrepreneur: '#f59e0b', military: '#34d399', unsure: '#94a3b8',
};
const money = (n) => (n == null ? '-' : `$${Math.round(n).toLocaleString()}`);

export const Chip = ({ label, hue }) => (
  <span className="rounded-full border px-2 py-0.5"
    style={{ borderColor: `${hue}55`, background: `${hue}12`, color: hue }}>{label}</span>
);

const friendlyDate = (raw) => {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (isNaN(d)) return null;
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays < 1) return 'Updated today';
    if (diffDays < 7) return `Updated ${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `Updated ${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    return `Updated ${d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
  } catch { return null; }
};

export default function PathwayIntelligencePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHS = user?.education_level === 'high_school' || !!user?.grade_level;
  const [entry, setEntry] = useState(null);
  const [mode, setMode] = useState('major');
  const [query, setQuery] = useState('');
  const [state, setState] = useState('');
  const [level, setLevel] = useState('');
  const [states, setStates] = useState([]);
  const [levels, setLevels] = useState([]);
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [income, setIncome] = useState(null);
  const [incomeInitialized, setIncomeInitialized] = useState(false);
  const [when, setWhen] = useState('by age 30');
  const [college, setCollege] = useState(true);
  const [venture, setVenture] = useState(false);
  const [paths, setPaths] = useState(null);
  const [loadingPaths, setLoadingPaths] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pathways/entry-points`, auth()).then((r) => {
      setEntry(r.data);
      if (!incomeInitialized && r.data?.already_known?.income?.value) {
        setIncome(r.data.already_known.income.value);
        setIncomeInitialized(true);
      } else if (!incomeInitialized) {
        setIncome(100000);
        setIncomeInitialized(true);
      }
    }).catch(() => { setEntry({ entry_points: [] }); if (!incomeInitialized) { setIncome(100000); setIncomeInitialized(true); } });
    axios.get(`${API}/pathways/states`, auth()).then((r) => setStates(r.data.states || [])).catch(() => {});
    axios.get(`${API}/pathways/credential-levels`, auth()).then((r) => setLevels(r.data.levels || [])).catch(() => {});
    axios.get(`${API}/pathways/coverage`, auth()).then((r) => setCoverage(r.data)).catch(() => {});
  }, []);

  const search = useCallback(async (q, st, lv) => {
    setSearching(true);
    try {
      const params = new URLSearchParams({ q: q || '' });
      if (st) params.set('state', st);
      if (lv) params.set('level', lv);
      const r = await axios.get(`${API}/pathways/majors?${params}`, auth());
      setResults(r.data.results || []);
      setSuggestions(r.data.suggestions || []);
    } finally { setSearching(false); }
  }, []);

  useEffect(() => { const t = setTimeout(() => search(query, state, level), 250); return () => clearTimeout(t); },
    [query, state, level, search]);

  const runIncome = async () => {
    setLoadingPaths(true);
    try {
      const r = await axios.post(`${API}/pathways/income-paths`, {
        target_income: Number(income), by_when: when,
        willing_to_attend_college: college, entrepreneurship_interest: venture,
      }, auth());
      setPaths(r.data);
    } finally { setLoadingPaths(false); }
  };

  if (!entry) return <AppLayout><LoadingSpinner /></AppLayout>;

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="pathway-intelligence-page">
        {/* ── Hero ── */}
        <div className="relative overflow-hidden rounded-2xl px-6 py-6 sm:px-8 sm:py-7"
          data-testid="pathway-hero"
          style={{
            background: 'rgba(6,23,54,0.72)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(57,227,220,0.22)',
            boxShadow: '0 0 30px -12px rgba(57,227,220,0.15)',
          }}>
          <div aria-hidden className="pointer-events-none absolute -left-32 -top-32 h-80 w-80 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(57,227,220,0.12), transparent 65%)' }} />
          <div aria-hidden className="pointer-events-none absolute -bottom-40 right-0 h-80 w-80 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(120,47,225,0.10), transparent 65%)' }} />
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(57,227,220,0.5), transparent)' }} />

          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-400/[0.06] px-3 py-1
              text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-200/80">
              <Sparkles className="h-3 w-3" style={{ filter: 'drop-shadow(0 0 4px rgba(57,227,220,0.5))' }} />
              {isHS ? 'Explore every path' : 'Every college, every state'}
            </span>
            <h1 className="font-outfit mt-3 text-2xl font-bold text-[#F4F7FF] sm:text-3xl">
              {isHS ? <>Find your <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">path</span></> :
                <>Find your <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">major</span></>}
            </h1>
            <p className="mt-2 max-w-xl text-sm text-[#C6D4E8]">
              {isHS
                ? 'Explore fields of study, career earnings, and education options to plan your next steps.'
                : 'See what graduates earn and where each major leads across every institution in the country.'}
            </p>

            {coverage && (
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="coverage-bar">
                <Metric n={coverage.majors} label="fields of study" />
                <Metric n={coverage.programmes} label="programmes" />
                <Metric n={coverage.institutions} label="institutions" />
                <Metric n={coverage.states} label="states & territories" />
                {friendlyDate(coverage.last_run) && (
                  <span className="text-[11px] text-[#C6D4E8]/60">
                    <Database className="mr-1 inline h-3 w-3 text-cyan-300/60" />
                    {friendlyDate(coverage.last_run)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <GlassCard className="p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Choose how to explore</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {entry.entry_points.map((e) => {
              const Icon = ICONS[e.key] || Compass;
              const hue = HUES[e.key];
              const known = entry.already_known?.[e.key];
              const active = mode === e.key;
              return (
                <button key={e.key} onClick={() => setMode(e.key)} data-testid={`entry-${e.key}`}
                  className="group rounded-xl border p-4 text-left transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    borderColor: active ? `${hue}88` : 'rgba(148,163,184,0.18)',
                    background: active ? `${hue}14` : 'rgba(255,255,255,0.02)',
                    boxShadow: active ? `0 0 30px -14px ${hue}` : 'none',
                  }}>
                  <Icon className="h-4 w-4" style={{ color: hue }} />
                  <p className="mt-2 text-sm font-semibold text-white">{e.label}</p>
                  <p className="mt-0.5 text-xs text-[#C6D4E8]">{known ? `From your profile: ${typeof known.value === 'number' ? money(known.value) : known.value}` : e.example}</p>
                </button>
              );
            })}
          </div>
        </GlassCard>

        {!['income', 'entrepreneur'].includes(mode) && (
          <GlassCard className="p-6" data-testid="major-search-panel">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex min-w-[280px] flex-1 items-center gap-3 rounded-xl px-4 py-3
                focus-within:shadow-[0_0_30px_-10px_#22d3ee] transition-all duration-200"
                style={{
                  border: '1px solid rgba(57,227,220,0.25)',
                  background: 'rgba(57,227,220,0.04)',
                }}>
                <Search className="h-4 w-4 text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 3px rgba(57,227,220,0.4))' }} />
                <input value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder={isHS ? "Search any field of study, career, or program..." : "Search any major in the country -- nursing, welding, astrophysics..."}
                  data-testid="major-search-input"
                  className="w-full bg-transparent text-sm text-white placeholder:text-[#C6D4E8]/50 focus:outline-none" />
              </div>
              <select value={state} onChange={(e) => setState(e.target.value)} data-testid="filter-state"
                className="rounded-xl border border-[rgba(57,227,220,0.2)] bg-[rgba(6,23,54,0.72)] px-3 py-3 text-xs text-white">
                <option value="">Every state</option>
                {states.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="filter-level"
                className="rounded-xl border border-[rgba(57,227,220,0.2)] bg-[rgba(6,23,54,0.72)] px-3 py-3 text-xs text-white">
                <option value="">Every level</option>
                {levels.map((l) => <option key={l.level} value={l.level}>{l.label}</option>)}
              </select>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="quick-picks">
              <span className="text-[11px] uppercase tracking-wider text-slate-300">Try</span>
              {['Nursing', 'Computer Science', 'Business', 'Psychology', 'Welding', 'Education', 'Biology', 'Cybersecurity']
                .map((t) => (
                  <button key={t} onClick={() => setQuery(t)} data-testid={`quick-${t.toLowerCase().replace(/\s/g, '-')}`}
                    className="rounded-full border border-white/12 px-3 py-1 text-[11px] text-slate-300 transition-all
                      hover:-translate-y-0.5 hover:border-cyan-300/50 hover:text-cyan-100
                      hover:shadow-[0_0_22px_-10px_#22d3ee]">{t}</button>
                ))}
              {(query || state || level) && (
                <button onClick={() => { setQuery(''); setLevel(''); }} data-testid="clear-filters"
                  className="ml-auto text-[11px] text-slate-300 hover:text-cyan-300">Clear</button>
              )}
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((m) => (
                <button key={m.cip4} onClick={() => navigate(`/app/pathways/majors/${m.cip4}${state ? `?state=${state}` : ''}`)}
                  data-testid={`major-card-${m.cip4}`}
                  className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] p-4 text-left
                    transition-all duration-200 hover:-translate-y-0.5 hover:border-cyan-300/50 hover:shadow-[0_0_36px_-16px_#22d3ee]">
                  <span aria-hidden className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full
                    opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.22), transparent 65%)' }} />
                  <p className="relative text-sm font-semibold text-white">{m.title}</p>
                  <p className="relative mt-0.5 text-[11px] text-slate-300">
                    CIP {m.cip_code} · {m.institution_count.toLocaleString()} {state ? `programmes in ${state}` : 'institutions'}
                    {!state && ` · ${m.state_count} states`}
                  </p>
                  {m.earnings_range && (
                    <p className="relative mt-2 text-[12px] font-medium text-cyan-200">
                      {m.earnings_range.low === m.earnings_range.high
                        ? `${money(m.earnings_range.low)} median graduate earnings`
                        : `${money(m.earnings_range.low)}-${money(m.earnings_range.high)}`}
                      <span className="font-normal text-slate-300">
                        {m.earnings_range.low === m.earnings_range.high ? '' : ' depending on level'}
                      </span>
                    </p>
                  )}
                  <div className="mt-2.5 flex flex-wrap gap-1">
                    {m.levels.slice(0, 6).map((d) => (
                      <span key={d.level} className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-slate-300">{d.label}</span>
                    ))}
                  </div>
                  <span className="mt-3 inline-flex items-center gap-1 text-[11px] text-cyan-300 opacity-0 transition-opacity group-hover:opacity-100">
                    Open intelligence <ArrowRight className="h-3 w-3" />
                  </span>
                </button>
              ))}
              {!results.length && !searching && (
                <div className="sm:col-span-2 lg:col-span-3" data-testid="no-results">
                  <p className="text-sm text-[#C6D4E8]">
                    No matching programmes found{state ? ` in ${state}` : ''}. Try a different spelling or browse the suggestions below.
                  </p>
                  {!!suggestions.length && (
                    <>
                      <p className="mt-2 text-[11.5px] text-slate-300">Closest fields that do:</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {suggestions.map((sg) => (
                          <button key={sg.cip4} onClick={() => navigate(`/app/pathways/majors/${sg.cip4}`)}
                            data-testid={`suggestion-${sg.cip4}`}
                            className="rounded-full border border-cyan-300/30 bg-cyan-400/[0.06] px-3 py-1.5 text-[11px] text-cyan-100
                              transition-colors hover:bg-cyan-400/15">
                            {sg.title} <span className="text-slate-300">· {sg.institution_count.toLocaleString()} schools</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </GlassCard>
        )}

        {['income', 'entrepreneur'].includes(mode) && (
          <GlassCard className="p-6" data-testid="income-panel">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">How much do you want to earn?</p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {[50000, 75000, 100000, 150000, 200000, 250000].map((v) => (
                <button key={v} onClick={() => setIncome(v)} data-testid={`income-${v}`}
                  className="rounded-full border px-3 py-1.5 text-xs transition-colors"
                  style={{
                    borderColor: income === v ? '#fbbf2488' : 'rgba(148,163,184,0.2)',
                    background: income === v ? 'rgba(251,191,36,0.12)' : 'transparent',
                    color: income === v ? '#fde68a' : '#cbd5e1',
                  }}>{money(v)}{v === 250000 ? '+' : ''}</button>
              ))}
              <input type="number" value={income || ''} onChange={(e) => setIncome(e.target.value)} data-testid="income-custom"
                className="w-32 rounded-lg border border-[rgba(57,227,220,0.2)] bg-[rgba(6,23,54,0.72)] px-3 py-1.5 text-xs text-white" />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {['right after high school', 'within 3 years', 'by age 25', 'by age 30', 'by age 40', 'eventually'].map((w) => (
                <button key={w} onClick={() => setWhen(w)} data-testid={`when-${w.replace(/\s/g, '-')}`}
                  className="rounded-full border px-3 py-1.5 text-xs transition-colors"
                  style={{
                    borderColor: when === w ? '#22d3ee88' : 'rgba(148,163,184,0.2)',
                    background: when === w ? 'rgba(34,211,238,0.1)' : 'transparent',
                    color: when === w ? '#a5f3fc' : '#cbd5e1',
                  }}>{w}</button>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-300">
              <label className="flex items-center gap-2" data-testid="toggle-college">
                <input type="checkbox" checked={college} onChange={(e) => setCollege(e.target.checked)} /> Open to college
              </label>
              <label className="flex items-center gap-2" data-testid="toggle-venture">
                <input type="checkbox" checked={venture} onChange={(e) => setVenture(e.target.checked)} /> I want to build a business
              </label>
              <button onClick={runIncome} data-testid="run-income-paths"
                className="ml-auto rounded-lg bg-gradient-to-r from-cyan-500 to-indigo-500 px-4 py-2 text-xs font-semibold text-white
                  shadow-[0_0_28px_-10px_#22d3ee] transition-transform hover:-translate-y-0.5">
                Show me the paths
              </button>
            </div>

            {loadingPaths && <div className="mt-6"><LoadingSpinner /></div>}
            {paths && !loadingPaths && (
              <div className="mt-6 space-y-3" data-testid="income-paths-results">
                <p className="text-sm text-white">Pathways where <span className="text-amber-300">{paths.target}</span> looks plausible</p>
                {paths.paths.map((p) => (
                  <div key={p.soc_code} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold text-white">{p.route}</p>
                      <span className="text-[11px] text-slate-300">{p.entry_education} · reaches your target at the {p.reach} wage</span>
                    </div>
                    <p className="mt-1.5 text-xs text-slate-300">{p.earnings_statement}</p>
                    <div className="mt-2.5 flex flex-wrap gap-2 text-[11px]">
                      <Chip label={`Opportunity ${p.opportunity_score.value}`} hue="#22d3ee" />
                      <Chip label={p.ai_impact.value.outlook} hue="#a78bfa" />
                      <Chip label={p.time_to_target} hue="#94a3b8" />
                    </div>
                  </div>
                ))}
                <p className="text-[11px] leading-relaxed text-slate-300">{paths.uncertainty}</p>
              </div>
            )}
          </GlassCard>
        )}

        <div className="flex flex-wrap items-center gap-3 text-xs text-[#C6D4E8]/50">
          <Layers className="h-3.5 w-3.5" />
          Education intelligence powered by College Scorecard, BLS Employment Projections, and O*NET data.
        </div>
      </div>
    </AppLayout>
  );
}

const Metric = ({ n, label }) => (
  <div className="min-w-[104px]">
    <p className="font-outfit text-2xl font-bold text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 8px rgba(57,227,220,0.25))' }}>
      {Number(n || 0).toLocaleString()}
    </p>
    <p className="text-[11px] uppercase tracking-wider text-[#C6D4E8]/60">{label}</p>
  </div>
);
